import React from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CmsInfoSection = ({ content, onChange }) => {
  const extraInfo = content?.extraInfo || {};
  const sections = extraInfo.sections || [];

  const updateExtraInfo = (field, value) => {
    onChange({
      ...content,
      extraInfo: { ...extraInfo, [field]: value },
    });
  };

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], [field]: value };
    updateExtraInfo('sections', updated);
  };

  const addSection = () => {
    const newSection = {
      id: Date.now(),
      heading: '',
      content: '',
      icon: 'Info',
    };
    updateExtraInfo('sections', [...sections, newSection]);
  };

  const removeSection = (index) => {
    updateExtraInfo('sections', sections.filter((_, i) => i !== index));
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const updated = [...sections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updateExtraInfo('sections', updated);
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-romantic-sm border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Extra Information</h3>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={extraInfo.enabled || false}
              onChange={(e) => updateExtraInfo('enabled', e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-foreground">Enable Info Page</span>
          </label>
        </div>

        {extraInfo.enabled && (
          <>
            <Input
              label="Page Title"
              type="text"
              placeholder="Additional Information"
              value={extraInfo.title || ''}
              onChange={(e) => updateExtraInfo('title', e.target.value)}
            />

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Info Sections</h4>
                <Button variant="outline" size="sm" onClick={addSection} iconName="Plus" iconPosition="left">
                  Add Section
                </Button>
              </div>

              <div className="space-y-3">
                {sections.map((section, idx) => (
                  <div key={section.id || idx} className="p-4 bg-background rounded-lg border border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-foreground">
                        {section.heading || `Section ${idx + 1}`}
                      </h5>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => moveSection(idx, 'up')}
                          disabled={idx === 0}
                          className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-romantic"
                        >
                          <Icon name="ChevronUp" size={16} />
                        </button>
                        <button
                          onClick={() => moveSection(idx, 'down')}
                          disabled={idx === sections.length - 1}
                          className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-romantic"
                        >
                          <Icon name="ChevronDown" size={16} />
                        </button>
                        <button
                          onClick={() => removeSection(idx)}
                          className="p-1 text-muted-foreground hover:text-error transition-romantic ml-2"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Icon Name"
                        type="text"
                        placeholder="e.g., Hotel, MapPin, Shirt"
                        value={section.icon || ''}
                        onChange={(e) => updateSection(idx, 'icon', e.target.value)}
                      />
                      <Input
                        label="Heading"
                        type="text"
                        placeholder="e.g., Travel & Accommodations"
                        value={section.heading || ''}
                        onChange={(e) => updateSection(idx, 'heading', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Content
                      </label>
                      <textarea
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-romantic min-h-[100px]"
                        placeholder="Multi-line content here...&#10;&#10;You can add multiple paragraphs."
                        value={section.content || ''}
                        onChange={(e) => updateSection(idx, 'content', e.target.value)}
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Line breaks will be preserved in the display
                      </p>
                    </div>
                  </div>
                ))}

                {sections.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No info sections yet. Click "Add Section" to create your first one.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CmsInfoSection;
